import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
  tokens,
  fmtMoney,
  SectionPanel,
  StatusPill,
  CodeChip,
  ClinicalEmptyState,
  ClosedLoopTracker,
} from "../../shared/clinical-design";
import { DENIAL_STATUS, DENIAL_STATUS_META, APPEAL_STEPS } from "../common/constants";

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 130 }}>
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: tokens.ink3,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: 13, color: tokens.ink, mt: 0.2 }}>{children}</Box>
  </Box>
);

/** Construye los pasos del closed-loop según el estado del denial. */
const buildSteps = (denial) => {
  const closed =
    denial.status === DENIAL_STATUS.RECOVERED || denial.status === DENIAL_STATUS.WRITTEN_OFF;
  const states = [
    "done",
    denial.rootCause ? "done" : "active",
    denial.appealFiledDate
      ? "done"
      : closed
      ? "blocked"
      : denial.rootCause
      ? "active"
      : "pending",
    closed ? "done" : denial.appealFiledDate ? "active" : "pending",
  ];
  return APPEAL_STEPS.map((label, i) => ({ label, state: states[i] }));
};

/**
 * Panel de detalle del denial: claim + CARC + causa raíz + checklist de
 * apelación (closed-loop) + acciones de workflow.
 *
 * @param {Object} denial - fila seleccionada (shape de fakeData)
 * @param {Function} onFileAppeal - (claimId) => void
 * @param {Function} onRecordOutcome - (claimId, outcome) => void
 * @param {boolean} saving
 */
export default function DenialDetail({ denial, onFileAppeal, onRecordOutcome, saving = false }) {
  if (!denial) {
    return (
      <SectionPanel title="Denial detail">
        <ClinicalEmptyState message="Select a denial from the worklist to review root cause and appeal status." />
      </SectionPanel>
    );
  }

  const statusMeta = DENIAL_STATUS_META[denial.status];
  const deadlineDays = dayjs(denial.appealDeadline).diff(dayjs(), "day");
  const deadlineTone = deadlineDays < 0 ? "muted" : deadlineDays <= 7 ? "crit" : deadlineDays <= 21 ? "warn" : "ok";
  const actionBtnSx = {
    textTransform: "none",
    fontSize: 12.5,
    color: tokens.primary,
    borderColor: tokens.primary,
    "&:hover": { borderColor: tokens.primary700, bgcolor: tokens.primary50 },
  };

  return (
    <Stack spacing={1.5}>
      <SectionPanel
        title="Denial detail"
        actions={<StatusPill tone={statusMeta.tone} label={statusMeta.label} dense />}
        contentSx={{ pt: 1.5 }}
      >
        <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
          <Field label="Claim">
            <CodeChip code={denial.id} />
          </Field>
          <Field label="Payer">{denial.payer}</Field>
          <Field label="Amount">
            <Typography
              component="span"
              sx={{
                fontFamily: tokens.fontMono,
                fontVariantNumeric: "tabular-nums",
                fontSize: 14,
                fontWeight: 600,
                color: tokens.ink,
              }}
            >
              {fmtMoney(denial.amount)}
            </Typography>
          </Field>
          <Field label="Patient">
            {denial.patientName}{" "}
            <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 11.5, color: tokens.ink3 }}>
              {denial.cipId}
            </Typography>
          </Field>
          <Field label="DOS">
            <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>
              {denial.dos}
            </Typography>
          </Field>
          <Field label="CPT / DX">
            <Stack direction="row" spacing={0.7}>
              <CodeChip code={denial.cpt} />
              <CodeChip code={denial.dx} />
            </Stack>
          </Field>
          <Field label="Denied on">
            <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>
              {denial.denialDate}
            </Typography>
          </Field>
          <Field label="Appeal deadline">
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>
                {denial.appealDeadline}
              </Typography>
              <StatusPill
                tone={deadlineTone}
                label={deadlineDays < 0 ? "Expired" : `${deadlineDays}d left`}
                dense
              />
            </Stack>
          </Field>
        </Stack>
      </SectionPanel>

      <SectionPanel title="CARC reason and root cause" contentSx={{ pt: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5 }}>
          <CodeChip code={denial.carc} tone="crit" />
          <Typography sx={{ fontSize: 13, color: tokens.ink }}>{denial.carcReason}</Typography>
        </Stack>
        <Field label="Root cause classification">{denial.rootCause || "Not classified yet"}</Field>
        <Box
          sx={{
            mt: 1.2,
            px: 1.5,
            py: 1,
            bgcolor: tokens.infoBg,
            border: `1px solid ${tokens.line}`,
            borderRadius: "4px",
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: tokens.info }}>
            Recommended action
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink, mt: 0.3 }}>
            {denial.recommendedAction}
          </Typography>
        </Box>
      </SectionPanel>

      <SectionPanel title="Appeal tracking" contentSx={{ pt: 1.5 }}>
        <ClosedLoopTracker steps={buildSteps(denial)} sx={{ mb: 1.5 }} />
        {denial.appealFiledDate && (
          <Typography sx={{ fontSize: 12.5, color: tokens.ink2, mb: 0.5 }}>
            Appeal filed on{" "}
            <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 12.5 }}>
              {denial.appealFiledDate}
            </Typography>
          </Typography>
        )}
        {denial.outcome && (
          <Typography sx={{ fontSize: 12.5, color: tokens.ink2, mb: 0.5 }}>
            Outcome: {denial.outcome}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {denial.status === DENIAL_STATUS.NEW && (
            <Button
              size="small"
              variant="outlined"
              disabled={saving || deadlineDays < 0}
              onClick={() => onFileAppeal(denial.id)}
              sx={actionBtnSx}
            >
              {saving ? "Saving..." : "File appeal"}
            </Button>
          )}
          {denial.status === DENIAL_STATUS.IN_APPEAL && (
            <>
              <Button
                size="small"
                variant="outlined"
                disabled={saving}
                onClick={() => onRecordOutcome(denial.id, DENIAL_STATUS.RECOVERED)}
                sx={actionBtnSx}
              >
                Mark recovered
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={saving}
                onClick={() => onRecordOutcome(denial.id, DENIAL_STATUS.WRITTEN_OFF)}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  color: tokens.ink2,
                  borderColor: tokens.lineStrong,
                  "&:hover": { borderColor: tokens.ink3, bgcolor: tokens.mutedBg },
                }}
              >
                Write off
              </Button>
            </>
          )}
        </Stack>
      </SectionPanel>
    </Stack>
  );
}
